import type { QuotePersistenceIdentity } from './store';

export interface QuoteAutosaveSaveInput<TSnapshot> {
  snapshot: TSnapshot;
  identity: QuotePersistenceIdentity | null;
  generation: number;
  change: number;
}

export interface QuoteAutosaveSavedEvent<TSnapshot> {
  snapshot: TSnapshot;
  identity: QuotePersistenceIdentity;
  previousIdentity: QuotePersistenceIdentity | null;
  generation: number;
  change: number;
}

export interface QuoteAutosaveQueueOptions<TSnapshot> {
  save: (
    input: QuoteAutosaveSaveInput<TSnapshot>,
  ) => Promise<QuotePersistenceIdentity>;
  isConflict: (error: unknown) => boolean;
  onRequestStart?: (input: QuoteAutosaveSaveInput<TSnapshot>) => void;
  onSaved?: (event: QuoteAutosaveSavedEvent<TSnapshot>) => void;
  onConflict?: (error: unknown, generation: number) => void;
  onError?: (error: unknown, generation: number) => void;
}

export interface QuoteAutosaveQueueState {
  generation: number;
  identity: QuotePersistenceIdentity | null;
  latestChange: number;
  persistedChange: number;
  isFlushing: boolean;
  hasPendingSnapshot: boolean;
  hasTerminalConflict: boolean;
}

export interface QuoteAutosaveQueue<TSnapshot> {
  startDocument: (
    identity: QuotePersistenceIdentity | null,
    baselineSnapshot?: TSnapshot,
  ) => number;
  reportSnapshot: (snapshot: TSnapshot) => number;
  flush: () => Promise<void>;
  getState: () => QuoteAutosaveQueueState;
}

/**
 * Serialisiert Angebots-Saves und koalesziert Zwischenstaende auf den jeweils
 * neuesten Snapshot. Die Queue kennt weder React noch den Offer-Store.
 */
export function createQuoteAutosaveQueue<TSnapshot>(
  options: QuoteAutosaveQueueOptions<TSnapshot>,
): QuoteAutosaveQueue<TSnapshot> {
  let generation = 0;
  let changeSequence = 0;
  let identity: QuotePersistenceIdentity | null = null;
  let latestSnapshot: TSnapshot | undefined;
  let latestChange = 0;
  let persistedChange = 0;
  let terminalConflict: unknown | undefined;
  let drainPromise: Promise<void> | null = null;

  const startDocument = (
    nextIdentity: QuotePersistenceIdentity | null,
    baselineSnapshot?: TSnapshot,
  ): number => {
    generation += 1;
    identity = nextIdentity;
    latestSnapshot = baselineSnapshot;
    latestChange = changeSequence;
    persistedChange = changeSequence;
    terminalConflict = undefined;
    return generation;
  };

  const reportSnapshot = (snapshot: TSnapshot): number => {
    changeSequence += 1;
    latestSnapshot = snapshot;
    latestChange = changeSequence;
    return latestChange;
  };

  const drain = async (): Promise<void> => {
    while (true) {
      if (terminalConflict !== undefined) {
        throw terminalConflict;
      }

      if (
        latestSnapshot === undefined
        || latestChange <= persistedChange
      ) {
        return;
      }

      const request: QuoteAutosaveSaveInput<TSnapshot> = {
        snapshot: latestSnapshot,
        identity,
        generation,
        change: latestChange,
      };

      let savedIdentity: QuotePersistenceIdentity;
      try {
        options.onRequestStart?.(request);
        savedIdentity = await options.save(request);
      } catch (error) {
        if (request.generation !== generation) {
          continue;
        }

        if (options.isConflict(error)) {
          terminalConflict = error;
          options.onConflict?.(error, request.generation);
        } else {
          options.onError?.(error, request.generation);
        }
        throw error;
      }

      if (request.generation !== generation) {
        continue;
      }

      const previousIdentity = identity;
      identity = savedIdentity;
      persistedChange = request.change;
      options.onSaved?.({
        snapshot: request.snapshot,
        identity: savedIdentity,
        previousIdentity,
        generation: request.generation,
        change: request.change,
      });
    }
  };

  const flush = (): Promise<void> => {
    if (terminalConflict !== undefined) {
      return Promise.reject(terminalConflict);
    }
    if (drainPromise !== null) {
      return drainPromise;
    }
    if (
      latestSnapshot === undefined
      || latestChange <= persistedChange
    ) {
      return Promise.resolve();
    }
    drainPromise = drain().finally(() => {
      drainPromise = null;
    });
    return drainPromise;
  };

  const getState = (): QuoteAutosaveQueueState => ({
    generation,
    identity,
    latestChange,
    persistedChange,
    isFlushing: drainPromise !== null,
    hasPendingSnapshot: (
      latestSnapshot !== undefined
      && latestChange > persistedChange
    ),
    hasTerminalConflict: terminalConflict !== undefined,
  });

  return {
    startDocument,
    reportSnapshot,
    flush,
    getState,
  };
}
